
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import type { BlogPost } from "@/types/blog";
import { CATEGORIES, COMMON_TAGS, blogService } from "@/services/blogService";
import { toast } from "sonner";
import { ArrowLeft, Eye, Image as ImageIcon, Plus, Save, Trash2, X } from "lucide-react";
import { isBrowser, safeLocalStorage } from "@/utils/runtime";
import { sanitizeHtml } from "@/utils/sanitizer";

// No need for EditorPost - we'll use BlogPost directly

interface PostEditorProps {
  postId?: number | string;
  onCancel: () => void;
}

const PostEditor: React.FC<PostEditorProps> = ({ postId, onCancel }) => {
  const isNewPost = !postId;
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Form state
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [slug, setSlug] = useState("");
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [imageUrl, setImageUrl] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [customTag, setCustomTag] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [published, setPublished] = useState(true);
  const [autoGenerateSlug, setAutoGenerateSlug] = useState(true);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [authEmail, setAuthEmail] = useState<string | null>(null);
  
  // Load post data if editing existing post
  useEffect(() => {
    const loadPost = async () => {
      if (postId) {
        try {
          const post = await blogService.getPostById(String(postId));
          if (post) {
            setTitle(post.title);
            setContent(post.content || "");
            setExcerpt(post.excerpt || "");
            setSlug(post.slug);
            setCategory(post.category || CATEGORIES[0]);
            setImageUrl(post.featured_image || post.cover_url || "");
            setTags(post.tags || []);
            setMetaDescription(post.seo_description || "");
            setPublished(post.status === "published");
            setAutoGenerateSlug(false);
          }
        } catch (error) {
          console.error("Error loading post:", error);
          toast.error("Erro ao carregar post");
        }
      }
    };
    
    loadPost();
  }, [postId]);
  
  // Auto-generate slug when title changes if auto-generate is enabled
  useEffect(() => {
    if (autoGenerateSlug && title) {
      setSlug(blogService.generateSlug(title));
    }
  }, [title, autoGenerateSlug]);

  // Auto-generate meta description from excerpt if empty
  useEffect(() => {
    if (excerpt && !metaDescription) {
      setMetaDescription(excerpt.substring(0, 160));
    }
  }, [excerpt, metaDescription]);

  // Load authenticated user email after mount
  useEffect(() => {
    if (!isBrowser) return;
    const auth = safeLocalStorage.getItem("toctoc_blog_auth");
    if (auth) {
      try {
        const authData = JSON.parse(auth);
        setAuthEmail(authData.email || null);
      } catch (error) {
        console.error("Error parsing auth data:", error);
      }
    }
  }, []);
  
  // Form field handlers
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value);
  const handleExcerptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => setExcerpt(e.target.value);
  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => setSlug(e.target.value);
  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => setContent(e.target.value);
  const handleImageUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => setImageUrl(e.target.value);
  const handleMetaDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => setMetaDescription(e.target.value);
  
  // Toggle auto-generate slug
  const toggleAutoGenerateSlug = () => {
    setAutoGenerateSlug(prev => !prev);
    if (!autoGenerateSlug && title) {
      setSlug(blogService.generateSlug(title));
    }
  };
  
  // Handle tags
  const toggleTag = (tag: string) => {
    setTags(prevTags => 
      prevTags.includes(tag)
        ? prevTags.filter(t => t !== tag)
        : [...prevTags, tag]
    );
  };

  const addCustomTag = () => {
    if (customTag && !tags.includes(customTag)) {
      setTags(prev => [...prev, customTag]);
      setCustomTag("");
    }
  };

  const removeTag = (tag: string) => {
    setTags(prevTags => prevTags.filter(t => t !== tag));
  };
  
  // Save post
  const handleSave = async (asDraft: boolean = false) => {
    if (!title || !content || !excerpt || !slug || !category || tags.length === 0) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }
    
    setIsSaving(true);
    
    try {
      const postData: BlogPost = {
        title,
        content,
        excerpt,
        slug,
        category,
        featured_image: imageUrl || "https://via.placeholder.com/800x400?text=Sem+Imagem",
        cover_url: imageUrl || "https://via.placeholder.com/800x400?text=Sem+Imagem",
        tags,
        seo_description: metaDescription,
        seo_title: title,
        author: authEmail?.split('@')[0] || "Admin",
        author_email: authEmail || "admin@toctoc.com",
        status: asDraft ? "draft" : "published",
        published_at: !asDraft ? new Date().toISOString() : null,
      };
      
      if (isNewPost) {
        await blogService.createPost(postData);
        toast.success("Post criado com sucesso!");
      } else if (postId) {
        await blogService.updatePost(String(postId), postData);
        toast.success("Post atualizado com sucesso!");
      }
      
      onCancel(); // Return to post list
    } catch (error) {
      console.error("Error saving post:", error);
      toast.error("Erro ao salvar o post");
    } finally {
      setIsSaving(false);
    }
  };
  
  // Delete post
  const handleDelete = async () => {
    if (!postId) return;
    
    if (isBrowser && globalThis.confirm("Tem certeza que deseja excluir este post? Esta ação não pode ser desfeita.")) {
      setIsDeleting(true);
      
      try {
        const success = await blogService.deletePost(String(postId));
        if (success) {
          toast.success("Post excluído com sucesso!");
          onCancel(); // Return to post list
        } else {
          toast.error("Erro ao excluir o post");
        }
      } catch (error) {
        console.error("Error deleting post:", error);
        toast.error("Erro ao excluir o post");
      } finally {
        setIsDeleting(false);
      }
    }
  };
  
  // Preview post
  const handlePreview = () => {
    setIsPreviewMode(!isPreviewMode);
  };

  // Preview component
  const PostPreview = () => {
    return (
      <div className="max-w-3xl mx-auto bg-white p-6 rounded-lg shadow">
        <Button 
          variant="outline" 
          className="mb-6"
          onClick={() => setIsPreviewMode(false)}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar para edição
        </Button>
        
        {imageUrl && (
          <div className="w-full h-64 mb-6 rounded-lg overflow-hidden">
            <img 
              src={imageUrl} 
              alt={title} 
              className="w-full h-full object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = "https://via.placeholder.com/800x400?text=Imagem+Inválida";
              }}
            />
          </div>
        )}
        
        <div className="mb-2 text-sm text-gray-500">
          {new Date().toLocaleDateString('pt-BR')} • {category}
        </div>
        
        <h1 className="text-3xl font-bold mb-4">{title || "Título do Post"}</h1>
        
        <div className="mb-8">
          {tags.map((tag, i) => (
            <span key={i} className="inline-block bg-gray-200 rounded-full px-3 py-1 text-sm font-semibold text-gray-700 mr-2 mb-2">
              #{tag}
            </span>
          ))}
        </div>
        
        <div className="prose max-w-none"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(content || "<p>O conteúdo do post aparecerá aqui...</p>") }}
        />
      </div>
    );
  };
  
  if (isPreviewMode) {
    return <PostPreview />;
  }
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button 
          variant="ghost" 
          onClick={onCancel}
          className="gap-1"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar
        </Button>
        
        <div className="flex items-center gap-2">
          {!isNewPost && (
            <Button 
              variant="outline" 
              type="button"
              onClick={handleDelete}
              disabled={isDeleting}
              className="gap-1 text-red-500 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
              {isDeleting ? "Excluindo..." : "Excluir"}
            </Button>
          )}
          
          <Button 
            variant="outline" 
            type="button"
            onClick={handlePreview}
            className="gap-1"
          >
            <Eye className="w-4 h-4" />
            Prévia
          </Button>
          
          <Button 
            variant="outline" 
            type="button"
            onClick={() => handleSave(true)}
            disabled={isSaving}
            className="gap-1"
          >
            <Save className="w-4 h-4" />
            Salvar Rascunho
          </Button>
          
          <Button 
            type="button"
            onClick={() => handleSave(false)}
            disabled={isSaving}
            className="gap-1 bg-toctoc-blue hover:bg-blue-700"
          >
            <Save className="w-4 h-4" />
            {isSaving ? "Salvando..." : "Publicar"}
          </Button>
        </div>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>{isNewPost ? "Criar novo post" : "Editar post"}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Título*</Label>
            <Input 
              id="title" 
              value={title} 
              onChange={handleTitleChange} 
              placeholder="Digite o título do post" 
              required
            />
          </div>
          
          {/* Image URL */}
          <div className="space-y-2">
            <Label htmlFor="image">URL da imagem de capa</Label>
            <div className="flex space-x-2">
              <div className="flex-1">
                <Input 
                  id="image" 
                  value={imageUrl} 
                  onChange={handleImageUrlChange} 
                  placeholder="https://exemplo.com/imagem.jpg" 
                />
              </div>
              <Button 
                variant="outline" 
                className="flex items-center gap-2"
                onClick={() => toast.info("Funcionalidade de upload em desenvolvimento. Por enquanto, utilize URLs de imagens existentes.")}
              >
                <ImageIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Escolher</span>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Deixe em branco para usar um placeholder
            </p>
          </div>
          
          {/* Preview image */}
          {imageUrl && (
            <div className="mt-2">
              <p className="text-sm font-medium mb-2">Prévia da imagem:</p>
              <div className="relative h-40 bg-gray-100 rounded overflow-hidden">
                <img 
                  src={imageUrl}
                  alt="Prévia" 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = "https://via.placeholder.com/800x400?text=Imagem+Inválida";
                  }}
                />
              </div>
            </div>
          )}
          
          {/* Category */}
          <div className="space-y-2">
            <Label htmlFor="category">Categoria*</Label>
            <Select value={category} onValueChange={setCategory} required>
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma categoria" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Tags */}
          <div className="space-y-2">
            <Label htmlFor="tags">Tags* (selecione pelo menos uma)</Label>
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {COMMON_TAGS.map((tag) => (
                  <div key={tag} className="flex items-center space-x-2">
                    <Checkbox 
                      id={`tag-${tag}`} 
                      checked={tags.includes(tag)}
                      onCheckedChange={() => toggleTag(tag)}
                    />
                    <label
                      htmlFor={`tag-${tag}`}
                      className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {tag}
                    </label>
                  </div>
                ))}
              </div>
              
              <div className="flex space-x-2">
                <Input
                  value={customTag}
                  onChange={(e) => setCustomTag(e.target.value)}
                  placeholder="Adicionar tag personalizada"
                  className="flex-1"
                />
                <Button
                  type="button"
                  onClick={addCustomTag}
                  variant="outline"
                  disabled={!customTag}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Selected tags */}
              <div className="flex flex-wrap gap-2 pt-2">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded flex items-center"
                  >
                    {tag}
                    <button 
                      type="button" 
                      onClick={() => removeTag(tag)}
                      className="ml-1 text-gray-500 hover:text-gray-700"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
            {tags.length === 0 && (
              <p className="text-sm text-red-500">Selecione pelo menos uma tag</p>
            )}
          </div>
          
          {/* Excerpt */}
          <div className="space-y-2">
            <Label htmlFor="excerpt">Resumo/Descrição curta*</Label>
            <Textarea 
              id="excerpt" 
              value={excerpt} 
              onChange={handleExcerptChange} 
              placeholder="Escreva um breve resumo do post (será exibido na listagem)" 
              className="resize-none"
              rows={2}
              required
            />
            <p className="text-xs text-muted-foreground">
              Máximo recomendado: 160 caracteres para SEO
            </p>
          </div>
          
          {/* Meta Description */}
          <div className="space-y-2">
            <Label htmlFor="metaDescription">Meta descrição (SEO)*</Label>
            <Textarea 
              id="metaDescription" 
              value={metaDescription} 
              onChange={handleMetaDescriptionChange} 
              placeholder="Descrição para motores de busca (Google, Bing, etc)" 
              className="resize-none"
              rows={2}
              required
            />
            <p className="text-xs text-muted-foreground">
              {metaDescription.length}/160 caracteres
              {metaDescription.length > 160 && (
                <span className="text-red-500"> (excedeu o limite recomendado)</span>
              )}
            </p>
          </div>
          
          {/* Slug */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="slug">Slug (URL)*</Label>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="auto-slug" 
                  checked={autoGenerateSlug}
                  onCheckedChange={toggleAutoGenerateSlug}
                />
                <label 
                  htmlFor="auto-slug" 
                  className="text-sm text-muted-foreground cursor-pointer"
                >
                  Gerar automaticamente
                </label>
              </div>
            </div>
            <Input 
              id="slug" 
              value={slug} 
              onChange={handleSlugChange} 
              placeholder="url-amigavel-do-post" 
              disabled={autoGenerateSlug}
              required
            />
            <p className="text-xs text-muted-foreground">
              URL do post: toctoc.com.br/blog/<span className="font-mono">{slug || 'slug-do-post'}</span>
            </p>
          </div>
          
          {/* Content */}
          <div className="space-y-2">
            <Label htmlFor="content">Conteúdo*</Label>
            <Textarea 
              id="content" 
              value={content} 
              onChange={handleContentChange} 
              placeholder="Escreva o conteúdo do post. Suporte básico a HTML como <h2>, <p>, <strong>, <em>, <ul>, <li>." 
              className="min-h-[200px]"
              required
            />
            <p className="text-xs text-muted-foreground">
              Em um editor real, este seria um editor WYSIWYG completo.
            </p>
          </div>
          
          {/* Published status */}
          <div className="flex items-center space-x-2 pt-4">
            <Switch 
              id="published" 
              checked={published}
              onCheckedChange={setPublished}
            />
            <Label htmlFor="published" className="cursor-pointer">
              {published ? "Publicar imediatamente" : "Salvar como rascunho"}
            </Label>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PostEditor;
